import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import TweetGenerator from '../TweetGenerator';
import ThreadGenerator from '../ThreadGenerator';
import SlideGenerator from '../SlideGenerator';
import VideoGenerator from '../VideoGeneratorRemote';
import BulkVideoGenerator from '../BulkVideoGenerator';
import VideoLibrary from './VideoLibrary';
import AutoMode from './AutoMode';
import VideoRebrand from '../VideoRebrand';
import InstagramPosting from '../InstagramPosting/InstagramPosting';
import MetaThreads from '../MetaThreads';
import Support from '../Support/Support';
import Settings from './Settings';
import MobileVideoGenerator from '../MobileVideoGenerator';
import useBackgroundVideoPreloader from '../../hooks/useBackgroundVideoPreloader';
import SubscriptionService from '../../services/SubscriptionService';
import PlanUpgrade from '../Subscription/PlanUpgrade';
import './Dashboard.css';

const Dashboard = ({ user, subscriptionStatus }) => {
  const [activeTab, setActiveTab] = useState('create');
  const [videos, setVideos] = useState([]);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const [bulkVideoJobs, setBulkVideoJobs] = useState([]);
  const [showSupport, setShowSupport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaModalData, setQuotaModalData] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Preload background videos early
  useBackgroundVideoPreloader();
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Subscribe to user's videos
    const videosRef = collection(db, 'users', user.uid, 'videos');
    const q = query(videosRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const videoList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVideos(videoList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user.uid]);

  const handleVideoGenerate = async (data) => {
    try {
      // Check if user can generate videos
      const canGenerate = await SubscriptionService.canGenerateVideo(user.uid);
      
      if (!canGenerate.canGenerate) {
        setQuotaModalData({
          videosRequested: 1,
          videosRemaining: 0,
          excessCount: 0,
          reason: canGenerate.reason
        });
        setShowQuotaModal(true);
        return;
      }

      // Check if this is a bulk batch (all jobs at once)
      if (data.type === 'bulk_batch') {
        // Additional validation for bulk generation
        const videosToGenerate = data.jobs.length;
        
        // Use the new bulk validation method
        const bulkValidation = await SubscriptionService.canGenerateBulkVideos(user.uid, videosToGenerate);
        
        if (!bulkValidation.canGenerate) {
          if (bulkValidation.excessCount) {
            setQuotaModalData({
              videosRequested: videosToGenerate,
              videosRemaining: bulkValidation.videosRemaining || 0,
              excessCount: bulkValidation.excessCount,
              reason: bulkValidation.reason
            });
            setShowQuotaModal(true);
          } else {
            setQuotaModalData({
              videosRequested: videosToGenerate,
              videosRemaining: 0,
              excessCount: 0,
              reason: bulkValidation.reason
            });
            setShowQuotaModal(true);
          }
          return;
        }
        
        console.log(`Starting bulk generation with ${data.jobs.length} jobs`);
        setBulkVideoJobs(data.jobs);
        setIsBulkGenerating(true);
        setGenerationProgress(0);
      } else if (data.isBulk) {
        // Legacy individual bulk job (shouldn't happen with new code)
        setBulkVideoJobs(prevJobs => [...prevJobs, data]);
        setIsBulkGenerating(true);
      } else {
        // Single video generation (existing logic)
        setVideoData(data);
        setIsGeneratingVideo(true);
        setGenerationProgress(0);
      }
    } catch (error) {
      console.error('Error checking video generation limits:', error);
      setQuotaModalData({
        videosRequested: 0,
        videosRemaining: 0,
        excessCount: 0,
        reason: 'Error checking subscription limits. Please try again.'
      });
      setShowQuotaModal(true);
    }
  };

  const handleVideoComplete = async () => {
    try {
      // Increment video usage for free plans
      await SubscriptionService.incrementVideoUsage(user.uid);
    } catch (error) {
      console.error('Error incrementing video usage:', error);
    }
    
    setIsGeneratingVideo(false);
    setVideoData(null);
    setGenerationProgress(0);
    // Switch to library tab to show the new video
    setActiveTab('library');
  };

  const handleBulkComplete = async (completedJobs) => {
    console.log(`Bulk generation complete! ${completedJobs.length} videos processed.`);
    
    try {
      // Count successful video generations
      const successfulVideos = completedJobs.filter(job => job.status === 'completed').length;
      
      // Increment video usage for each successful video (only for free plans)
      for (let i = 0; i < successfulVideos; i++) {
        await SubscriptionService.incrementVideoUsage(user.uid);
      }
      
      console.log(`Incremented video usage count by ${successfulVideos} videos`);
    } catch (error) {
      console.error('Error incrementing bulk video usage:', error);
    }
    
    setIsBulkGenerating(false);
    setBulkVideoJobs([]);
    setGenerationProgress(0);
    // Switch to library tab to show the new videos
    setActiveTab('library');
  };

  const handleUpgradeComplete = async (upgradeData) => {
    setShowUpgrade(false);
    // Refresh the page to update subscription status
    window.location.reload();
  };

  const handleDeleteVideo = async (videoId, videoUrl) => {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'users', user.uid, 'videos', videoId));
      
      // Delete from Storage
      if (videoUrl) {
        const videoRef = ref(storage, videoUrl);
        await deleteObject(videoRef);
      }
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };

  // Generate fallback avatar with first letter of email
  const generateFallbackAvatar = (email, displayName) => {
    const firstLetter = (displayName?.charAt(0) || email?.charAt(0) || 'U').toUpperCase();
    return (
      <div className="fallback-avatar">
        {firstLetter}
      </div>
    );
  };

  return (
    <div className="dashboard">
      {/* Support Page */}
      {showSupport && (
        <Support 
          user={user} 
          onClose={() => setShowSupport(false)} 
        />
      )}

      {/* Settings Page */}
      {showSettings && (
        <Settings 
          user={user} 
          onClose={() => setShowSettings(false)} 
        />
      )}

      {/* Plan Upgrade Modal */}
      {showUpgrade && subscriptionStatus && (
        <PlanUpgrade 
          user={user}
          currentSubscription={subscriptionStatus}
          onUpgradeComplete={handleUpgradeComplete}
          onClose={() => setShowUpgrade(false)}
        />
      )}

      {/* Quota Limit Modal */}
      {showQuotaModal && quotaModalData && (
        <div className="modal-overlay" onClick={() => setShowQuotaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-header-quota">Monthly Quota Exceeded</h2>
            
            <p>
              You're trying to generate <strong>{quotaModalData.videosRequested}</strong> videos, 
              but you only have <strong>{quotaModalData.videosRemaining}</strong> videos remaining this month.
            </p>

            {quotaModalData.excessCount > 0 && (
              <div className="quota-details">
                <div className="quota-item">
                  <span className="quota-label">Videos Requested:</span>
                  <span className="quota-value">{quotaModalData.videosRequested}</span>
                </div>
                <div className="quota-item">
                  <span className="quota-label">Videos Remaining:</span>
                  <span className="quota-value">{quotaModalData.videosRemaining}</span>
                </div>
                <div className="quota-item excess">
                  <span className="quota-label">Videos Over Limit:</span>
                  <span className="quota-value">{quotaModalData.excessCount}</span>
                </div>
              </div>
            )}

            <div className="upgrade-hint">
              <p><strong>Want unlimited videos?</strong></p>
              <p>Upgrade to Pro and generate as many videos as you want, no monthly limits!</p>
            </div>

            <p>
              {quotaModalData.excessCount > 0 
                ? `Please remove ${quotaModalData.excessCount} video(s) from your bulk generation or upgrade your plan to continue.`
                : 'Upgrade your plan to continue generating videos.'
              }
            </p>

            <div className="modal-actions">
              <button 
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowQuotaModal(false)}
              >
                {quotaModalData.excessCount > 0 ? 'Remove Videos' : 'Close'}
              </button>
              <button 
                className="modal-btn modal-btn-primary"
                onClick={() => {
                  setShowQuotaModal(false);
                  setShowUpgrade(true);
                }}
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen overlay for video generation */}
      {isGeneratingVideo && (
        <div className="generation-overlay">
          <div className="generation-content">
            <div className="generation-animation">
              <div className="pulse-ring"></div>
              <div className="pulse-ring"></div>
              <div className="pulse-ring"></div>
              <div className="generation-icon">⚡</div>
            </div>
            <h2>Creating Your Viral Video</h2>
            <p>This will only take a moment...</p>
            
            <VideoGenerator 
              tweetImage={videoData.imageData}
              tweetData={videoData.tweetData}
              isGenerating={isGeneratingVideo}
              onVideoComplete={handleVideoComplete}
              user={user}
              autoStart={true}
              onProgressUpdate={setGenerationProgress}
            />
          </div>
        </div>
      )}

      {/* Full-screen overlay for bulk video generation */}
      {isBulkGenerating && (
        <div className="generation-overlay">
          <div className="generation-content">
            <div className="generation-animation">
              <div className="pulse-ring"></div>
              <div className="pulse-ring"></div>
              <div className="pulse-ring"></div>
              <div className="generation-icon">⚡</div>
            </div>
            <h2>Creating Multiple Viral Videos</h2>
            <p>Processing {bulkVideoJobs.length} videos in parallel...</p>
            
            <BulkVideoGenerator 
              videoJobs={bulkVideoJobs}
              user={user}
              onAllComplete={handleBulkComplete}
              onProgressUpdate={setGenerationProgress}
            />
          </div>
        </div>
      )}

      {!isMobile && (
        <div className="dashboard-sidebar">
          <div className="dashboard-header">
            <div className="dashboard-logo">
              <img src="/logo.png" alt="Logo" className="logo-image" />
            </div>
          </div>

          <nav className="dashboard-nav">
            <button 
              className={`nav-item ${activeTab === 'create' ? 'active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              <span className="nav-icon">✨</span>
              <span className="nav-text">Generate</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'threads' ? 'active' : ''}`}
              onClick={() => setActiveTab('threads')}
            >
              <span className="nav-icon">🧵</span>
              <span className="nav-text">Threads</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'slides' ? 'active' : ''}`}
              onClick={() => setActiveTab('slides')}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-text">Slides</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'rebrand' ? 'active' : ''}`}
              onClick={() => setActiveTab('rebrand')}
            >
              <span className="nav-icon">🎭</span>
              <span className="nav-text">Rebrand</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => setActiveTab('library')}
            >
              <span className="nav-icon">📚</span>
              <span className="nav-text">Library</span>
              {videos.length > 0 && (
                <span className="nav-badge">{videos.length}</span>
              )}
            </button>
            <button 
              className={`nav-item ${activeTab === 'auto' ? 'active' : ''}`}
              onClick={() => setActiveTab('auto')}
            >
              <span className="nav-icon">🤖</span>
              <span className="nav-text">Auto Mode</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'posting' ? 'active' : ''}`}
              onClick={() => setActiveTab('posting')}
            >
              <span className="nav-icon">📱</span>
              <span className="nav-text">Posting</span>
              <span className="beta-indicator">Beta</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'meta-threads' ? 'active' : ''}`}
              onClick={() => setActiveTab('meta-threads')}
            >
              <span className="nav-icon">🧵</span>
              <span className="nav-text">Meta Threads</span>
            </button>
          </nav>

          <div className="support-section">
            <button 
              className="support-tab"
              onClick={() => setShowSupport(true)}
            >
              <span className="support-icon">💬</span>
              <span className="support-text">Support</span>
            </button>
          </div>

          <div className="dashboard-footer">
            <div className="user-card">
              {user.photoURL ? (
                <img 
                  src={user.photoURL} 
                  alt="Profile"
                  className="user-avatar"
                />
              ) : (
                generateFallbackAvatar(user.email, user.displayName)
              )}
              <div className="user-info">
                <p className="user-name">{user.displayName || 'User'}</p>
                <p className="user-email">{user.email}</p>
                {subscriptionStatus && (
                  <p className="user-plan">
                    {SubscriptionService.getSubscriptionDisplayInfo(subscriptionStatus).title}
                    {subscriptionStatus.subscriptionType === 'free' && !subscriptionStatus.isUnlimited && (
                      <span className="plan-usage"> • {subscriptionStatus.videosUsedThisMonth}/{subscriptionStatus.maxVideosPerMonth} videos</span>
                    )}
                  </p>
                )}
              </div>
              {subscriptionStatus && (
                <button 
                  className="compact-upgrade-btn"
                  onClick={() => setShowUpgrade(true)}
                  title={subscriptionStatus.subscriptionType === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                >
                  {subscriptionStatus.subscriptionType === 'free' ? '⬆️' : '⚙️'}
                </button>
              )}
            </div>
            <button 
              className="settings-btn"
              onClick={() => {
                console.log('🔄 Opening settings - forcing profile refresh');
                setShowSettings(true);
              }}
            >
              <span className="settings-icon">⚙️</span>
              <span>Settings</span>
            </button>
          </div>
        </div>
      )}

      <div className={`dashboard-main ${isMobile ? 'mobile-main' : ''}`}>
        {isMobile && (
          <div className="mobile-dashboard-header">
            <div className="mobile-user-info">
              <span className="mobile-user-name">{user.displayName || 'User'}</span>
              <button 
                className="mobile-settings-btn"
                onClick={() => setShowSettings(true)}
              >
                ⚙️
              </button>
            </div>
            <div className="mobile-nav">
              <button 
                className={`mobile-nav-btn ${activeTab === 'create' ? 'active' : ''}`}
                onClick={() => setActiveTab('create')}
              >
                ✨ Generate
              </button>
              <button 
                className={`mobile-nav-btn ${activeTab === 'meta-threads' ? 'active' : ''}`}
                onClick={() => setActiveTab('meta-threads')}
              >
                🧵 Meta Threads
              </button>
              <button 
                className={`mobile-nav-btn ${activeTab === 'library' ? 'active' : ''}`}
                onClick={() => setActiveTab('library')}
              >
                📚 Library ({videos.length})
              </button>
            </div>
          </div>
        )}
        
                  {activeTab === 'create' && (
            <div className="create-section">
              {isMobile ? (
                <MobileVideoGenerator 
                  onVideoGenerate={handleVideoGenerate}
                  user={user}
                />
              ) : (
                <>
                  <div className="page-header">
                    <h1>Create Viral Videos</h1>
                    <p>Transform tweets into engaging video content</p>
                  </div>

                  <div className="generator-container">
                    <TweetGenerator 
                      onVideoGenerate={handleVideoGenerate} 
                      user={user}
                      onUpgradeRequest={() => setShowUpgrade(true)}
                    />
                  </div>
                </>
              )}
            </div>
          )}

        {activeTab === 'threads' && (
          <div className="threads-section">
            <div className="page-header">
              <h1>Thread Mode</h1>
              <p>Generate videos from tweet threads and copy thread content</p>
            </div>

            <div className="generator-container">
              <ThreadGenerator 
                onVideoGenerate={handleVideoGenerate}
                user={user}
              />
            </div>
          </div>
        )}

        {activeTab === 'slides' && (
          <div className="slides-section">
            <SlideGenerator user={user} />
          </div>
        )}

        {activeTab === 'rebrand' && (
          <div className="rebrand-section">
            <div className="page-header">
              <h1>🎭 Video Rebrand</h1>
              <p>Subtly modify TikTok videos to avoid algorithmic detection</p>
            </div>

            <div className="generator-container">
              <VideoRebrand user={user} />
            </div>
          </div>
        )}

        {activeTab === 'library' && (
          <div className="library-section">
            {isMobile ? (
              <div className="mobile-library">
                <div className="mobile-library-header">
                  <h2>Your Videos ({videos.length})</h2>
                  <button 
                    className="mobile-back-btn"
                    onClick={() => setActiveTab('create')}
                  >
                    ← Back
                  </button>
                </div>
                {loading ? (
                  <div className="mobile-loading">
                    <div className="spinner"></div>
                    <p>Loading videos...</p>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="mobile-empty">
                    <span className="empty-icon">📹</span>
                    <h3>No videos yet</h3>
                    <p>Generate your first video to see it here</p>
                  </div>
                ) : (
                  <div className="mobile-video-list">
                    {videos.map((video) => (
                      <div key={video.id} className="mobile-video-item">
                        <div className="mobile-video-info">
                          <h4>{video.title || 'Untitled Video'}</h4>
                          <p>{new Date(video.createdAt?.toDate()).toLocaleDateString()}</p>
                        </div>
                        <div className="mobile-video-actions">
                          <button 
                            className="mobile-download-btn"
                            onClick={() => window.open(video.downloadUrl, '_blank')}
                          >
                            📥
                          </button>
                          <button 
                            className="mobile-delete-btn"
                            onClick={() => handleDeleteVideo(video.id, video.downloadUrl)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="page-header">
                  <h1>Your Videos</h1>
                  <p>Manage and download your creations</p>
                </div>

                <VideoLibrary 
                  videos={videos}
                  loading={loading}
                  onDeleteVideo={handleDeleteVideo}
                />
              </>
            )}
          </div>
        )}

        {activeTab === 'auto' && (
          <div className="auto-section">
            <AutoMode user={user} />
          </div>
        )}

        {activeTab === 'posting' && (
          <div className="posting-section">
            <InstagramPosting user={user} />
          </div>
        )}

        {activeTab === 'meta-threads' && (
          <div className="meta-threads-section">
            <MetaThreads user={user} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 